import React, { Suspense, lazy, useEffect } from "react";
import { ToastContainer } from "react-toastify";
import { ThemeProvider } from "@mui/material";
import theme from "./theme";
import CssBaseline from "@mui/material/CssBaseline";
import { Routes, Route } from "react-router-dom";
import useTitle from "./hooks/useTitle";
import Home from "./pages/Home/Home";
import TermsAndConditions from "./pages/TermsAndConditions/TermsAndConditions";
import { LoadingOverlay } from "./components/LoadingOverlay";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentToken, setCredentials } from "./features/auth/authSlice";
import { useRefreshMutation } from "./features/auth/authApiSlice";
import jwtDecode from "jwt-decode";

// Import error boundary and error logger
import ErrorBoundary from "./components/ErrorBoundary";
import { setupGlobalErrorHandlers } from "./services/frontendErrorLogger";
import setupConsoleErrorCapture from "./services/consoleErrorCapture";

// Import token refresh manager
import { TokenRefreshManager } from "./features/auth/tokenRefresh";

// Import session expired modal
import SessionExpiredModal from "./components/SessionExpiredModal";

// Import pdfjs-dist and set the worker source 
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { connectSocket } from "./config/socket";
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js"; 

const AppPageComponents = lazy(() => import("./AppPageComponents"));
const AIChatBubble = lazy(() => import("./features/aiChat/AIChatBubble"));

function App() {
  useTitle("Bidd");
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const [refresh] = useRefreshMutation();

  // Setup global error handlers and console error capture
  useEffect(() => {
    const cleanupErrorHandlers = setupGlobalErrorHandlers();
    const cleanupConsoleErrorCapture = setupConsoleErrorCapture();
    
    return () => {
      cleanupErrorHandlers(); // Cleanup on unmount
      cleanupConsoleErrorCapture(); // Cleanup console override
    }
  }, []);

  // Persistent auth check
  useEffect(() => {
    // Only use Redux state for access token
    if (token) {
      // If we have a token in Redux, try to verify it's still valid
      try {
        const decoded = jwtDecode(token);
        const expTime = decoded.exp * 1000; // convert to milliseconds
        
        if (expTime < Date.now() + 5 * 60 * 1000) { // token expires in less than 5 minutes
          //console.log("Token expiring soon, refreshing");
          handleRefresh();
        }
      } catch (e) {
        //console.log("Token verification failed, trying refresh");
        handleRefresh();
      }
    } else if (document.cookie.includes('jwt')) {
      // We have a refresh token cookie but no access token
      //console.log("Found refresh cookie, trying to get new access token");
      handleRefresh();
    }
  }, []);

    useEffect(() => {
    // Only connect to socket if we have a valid token
    if (token) {
      try {
        const decoded = jwtDecode(token);
        // Check if token is valid and not expired
        if (decoded.exp * 1000 > Date.now()) {
          const userId = decoded.UserInfo.id;
          const companyId = decoded.UserInfo.company;
          connectSocket(token, userId, companyId);
        } else {
          // Token is expired, don't connect socket
          //console.log("Not connecting socket - token expired");
        }
      } catch (e) {
        // Token is invalid, don't connect socket
        //console.log("Not connecting socket - invalid token", e);
      }
    }
  }, [token]);
  
  const handleRefresh = async () => {
    try {
      const result = await refresh().unwrap();
      if (result?.accessToken) {
        //console.log("Token refresh successful");
        dispatch(setCredentials({ accessToken: result.accessToken }));
      }
    } catch (err) {
      console.error("Token refresh failed:", err);
    }
  };

  // Only show chat bubble when user is authenticated
  const shouldShowChatBubble = !!token;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer />
      {/* Token refresh manager - keeps tokens fresh in the background */}
      <TokenRefreshManager />
      {/* Session expired modal - shows when session expires */}
      <SessionExpiredModal />
      <ErrorBoundary>
        <Routes>
          <Route index element={<Home />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route
            path="/*"
            element={
              <Suspense fallback={<LoadingOverlay />}>
                <ErrorBoundary>
                  <AppPageComponents />
                </ErrorBoundary>
              </Suspense>
            }
          />
        </Routes>
        
        {/* AI Chat Bubble - Only shown when user is authenticated */}
        {shouldShowChatBubble && (
          <Suspense fallback={null}>
            <AIChatBubble />
          </Suspense>
        )}
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;